import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ReactFlowProvider } from 'reactflow';
import useStore from '../store/useStore';
import Toolbar from '../components/Toolbar';
import Canvas from '../components/Canvas';
import Sidebar from '../components/Sidebar';
import { apiPost, apiGet } from '../api';
import { useT } from '../i18n';

export default function EditorPage() {
  const t = useT();
  const { draftId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const templateFromState = location.state?.template;
  const theme = useStore((s) => s.theme);
  const accessToken = useStore((s) => s.accessToken);
  const generateJSON = useStore((s) => s.generateJSON);
  const resetEditor = useStore((s) => s.resetEditor);
  const restoreFromJSON = useStore((s) => s.restoreFromJSON);
  const addEntityNode = useStore((s) => s.addEntityNode);
  const addEnumNode = useStore((s) => s.addEnumNode);
  const language = useStore((s) => s.language);
  const selectedNodeId = useStore((s) => s.selectedNodeId);
  const readOnly = useStore((s) => s.readOnly);
  const relationHoverMode = useStore((s) => s.relationHoverMode);
  const setRelationHoverMode = useStore((s) => s.setRelationHoverMode);
  const projectName = useStore((s) => s.projectName);
  const setProjectConfig = useStore((s) => s.setProjectConfig);
  const [menuCollapsed, setMenuCollapsed] = useState(() => {
    return localStorage.getItem('editorSidebar') !== 'open';
  });

  const toggleMenu = () => {
    const next = !menuCollapsed;
    setMenuCollapsed(next);
    localStorage.setItem('editorSidebar', next ? 'closed' : 'open');
  };
  const [currentDraftId, setCurrentDraftId] = useState(null);
  const [saveStatus, setSaveStatus] = useState('');
  const [loaded, setLoaded] = useState(false);
  const saveTimerRef = useRef(null);
  const lastSavedRef = useRef('');

  const undo = useStore((s) => s.undo);

  useEffect(() => {
    if (!accessToken) navigate('/');
  }, [accessToken, navigate]);

  // Ctrl+Z undo
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo]);

  // Load project/draft or reset for new
  useEffect(() => {
    if (loaded) return;

    if (draftId) {
      loadProject(draftId);
    } else if (templateFromState) {
      // Loaded from a template selection — populate editor and clear router state.
      restoreFromJSON(templateFromState);
      setCurrentDraftId(null);
      setLoaded(true);
      // Auto-save will kick in when the user makes their first change.
      navigate(location.pathname, { replace: true, state: null });
    } else {
      resetEditor();
      setCurrentDraftId(null);
      setLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId]);

  const setConfig = useStore((s) => s.setProjectConfig);

  const loadProject = async (id) => {
    try {
      const res = await apiGet(`/api/v1/projects/${id}`);
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.data?.projectJson) {
          const parsed = JSON.parse(data.data.projectJson);
          restoreFromJSON(parsed);
          setConfig('projectId', id);
          setCurrentDraftId(id);
          lastSavedRef.current = JSON.stringify(parsed);
          setLoaded(true);
          return;
        }
      }
    } catch { /* ignore */ }

    // If not found, reset
    resetEditor();
    setLoaded(true);
  };

  // Auto-save to project
  const saveDraft = useCallback(async () => {
    if (!accessToken || !loaded || readOnly) return;
    const nodes = useStore.getState().nodes;
    if (!currentDraftId && nodes.length === 0) return;
    const json = generateJSON();
    const jsonStr = JSON.stringify(json);
    if (jsonStr === lastSavedRef.current) return;

    setSaveStatus('saving');
    try {
      const url = currentDraftId
        ? `/api/v1/projects/drafts?id=${currentDraftId}`
        : '/api/v1/projects/drafts';
      const res = await apiPost(url, json);
      const data = await res.json();
      if (data.success) {
        lastSavedRef.current = jsonStr;
        if (!currentDraftId && data.data?.id) {
          setCurrentDraftId(data.data.id);
          setConfig('projectId', data.data.id);
        }
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus(''), 2000);
      } else {
        setSaveStatus('error');
      }
    } catch {
      setSaveStatus('error');
    }
  }, [accessToken, generateJSON, currentDraftId, loaded, readOnly]);

  const triggerAutoSave = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveDraft();
    }, 3000);
  }, [saveDraft]);

  useEffect(() => {
    if (!loaded) return;
    const unsub = useStore.subscribe(() => {
      triggerAutoSave();
    });
    return () => {
      unsub();
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [triggerAutoSave, loaded]);

  if (!loaded) {
    return (
      <div className={`app ${theme}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ color: 'var(--text-secondary)' }}>{t('home.loading')}</span>
      </div>
    );
  }

  return (
    <ReactFlowProvider>
      <div className={`app ${theme}`}>
        <div className="main-layout">
          <Toolbar collapsed={menuCollapsed} onToggle={toggleMenu} />
          <div className="main-area">
            <Canvas />
            <Sidebar />
          </div>
        </div>
        {/* Floating project name input — top-left, slides when toolbar opens */}
        {language && !readOnly && (
          <div className={`floating-project-name ${menuCollapsed ? '' : 'toolbar-open'}`}>
            <input
              type="text"
              className="floating-project-name-input"
              value={projectName}
              onChange={(e) => setProjectConfig('projectName', e.target.value)}
              placeholder={t('toolbar.projectName')}
              spellCheck={false}
            />
          </div>
        )}
        {/* Floating add buttons */}
        {language && !readOnly && (
          <div className={`floating-add-buttons ${selectedNodeId ? 'sidebar-open' : ''}`}>
            <button className="floating-add-btn" onClick={() => addEntityNode()} title="Add Table">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M7 2v10M2 7h10" />
              </svg>
              {t('toolbar.table')}
            </button>
            <button className="floating-add-btn enum" onClick={() => addEnumNode()} title="Add Enum">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M7 2v10M2 7h10" />
              </svg>
              {t('toolbar.enum')}
            </button>
            <div className="floating-divider" aria-hidden="true" />
            <div className="hover-mode-toggle" title={t('hoverMode.tooltip')}>
              <span className="hover-mode-label">{t('hoverMode.label')}</span>
              <button
                className={`hover-mode-btn ${relationHoverMode === 'code' ? 'active' : ''}`}
                onClick={() => setRelationHoverMode('code')}
              >
                {t('hoverMode.code')}
              </button>
              <button
                className={`hover-mode-btn ${relationHoverMode === 'json' ? 'active' : ''}`}
                onClick={() => setRelationHoverMode('json')}
              >
                {t('hoverMode.json')}
              </button>
            </div>
          </div>
        )}
        {/* Save indicator */}
        <div className={`save-indicator ${saveStatus}`}>
          {saveStatus === 'saving' && t('save.saving')}
          {saveStatus === 'saved' && t('save.saved')}
          {saveStatus === 'error' && t('save.error')}
        </div>
        {/* Back button - shifts when sidebar open */}
        <button
          className={`back-to-home ${selectedNodeId ? 'sidebar-open' : ''}`}
          onClick={async () => {
            if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
            await saveDraft();
            navigate('/home');
          }}
          title="Back to dashboard"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M12 8H4M4 8l4-4M4 8l4 4" />
          </svg>
        </button>
      </div>
    </ReactFlowProvider>
  );
}
